import { BaseApi } from "lib/core/apiClient";

/**
 * Bump the -vN suffix to reset every browser: nothing reads the old key again.
 * Keep the flo-anon-id prefix - writeAnonymousId sweeps older versions by matching on it.
 */
const anonIdKey = String(import.meta.env.VITE_FP_ANON_ID_KEY ?? "flo-anon-id-v5");
const envAnonIdExpiryDays = Number(import.meta.env.VITE_FP_ANON_ID_EXPIRY_DAYS);
/** Missing or malformed falls back to 30 days rather than caching forever. */
const anonIdExpiryDays = Number.isFinite(envAnonIdExpiryDays) ? envAnonIdExpiryDays : 30;

/** Kill switch. Fingerprinting was last disabled by 842f54c970 after saturating the checkout RDS. */
export const disableFingerprint = false;

/** fingerprint-service endpoints, relative to VITE_FP_API_URI. Both take the same body. */
export const fingerprintPaths = {
  RESOLVE: "/resolve",
  MAP_USER: "/map-user",
};

export const constants = {
  AUTH_COOKIE_CLIENT: "flo_auth",
  REFRESH_TOKEN_COOKIE_CLIENT: "flo_refresh",
  AUTH_LOGGED_OUT: "flo_auth_logged_out",
  AUTH_TOKEN_HEADER_SERVER: "Authorization",
  AUTH_COOKIE_CLIENT_EXPIRY: 1 / 32,
  REFRESH_TOKEN_COOKIE_CLIENT_EXPIRY: 365,
  BUREAU_USER_REGISTERED_EXPIRY: 30,
  LOCAL_STORAGE_AUTH_COOKIE_CLIENT_EXPIRY: "flo_auth_expiry",
  LOCAL_STORAGE_REFRESH_TOKEN_COOKIE_CLIENT_EXPIRY: "flo_refresh_expiry",
  LOCAL_STORAGE_TWO_STEP_AUTH_EXPIRY: "flo_two_step_auth_expiry",
  /** Always read it via readAnonymousId - a bare read ignores the expiry. */
  LOCAL_STORAGE_FLO_ANONYMOUS_ID: anonIdKey,
  /** Both derived, so bumping the ID key moves them with it. */
  LOCAL_STORAGE_FLO_ANONYMOUS_ID_EXPIRY: `${anonIdKey}-expiry`,
  LOCAL_STORAGE_FLO_ANON_ID_MAPPED: `${anonIdKey}-mapped`,
  /** Days a cached anonymous ID is trusted before we re-resolve it. 0 caches nothing. */
  FLO_ANONYMOUS_ID_EXPIRY_DAYS: anonIdExpiryDays,
  ENV_PROD: "production",
  ENV_DEV: "development",
  ENV_TEST: "staging",
  PAYMENT_FAILURE_THRESHOLD: 5,
  FLO_SESSION_ID_XHR_HEADER: "x-shopflo-session",
  FLO_SESSION_ID_COOKIE: "flo_session_id",
  TWO_STEP_AUTH_COOKIE_CLIENT: "flo_two_step_auth",
  ORDER_HISTORY_COOKIE_CLIENT: "flo_order_history",
  CHECKOUT_HOST: import.meta.env.VITE_CHECKOUT_HOST,
  EXPERIMENT_SELECTED_TEST_GROUP: "flo_experiment_selected_group_id",
  FLO_BUREAU_USER_ID: "FLO_BUREAU_USER_ID",
  FLO_BUREAU_USER_REGISTERED: "flo_bureau_user_registered",
};

export const eventTypes = {
  ANALYTICS: "analytics",
  CLOSE_IFRAME: "CLOSE_IFRAME",
  CLOSE_CART_IFRAME: "CLOSE_CART_IFRAME",
  CLOSE_IFRAME_BUYNOW: "CLOSE_IFRAME_BUYNOW",
  SET_COOKIE: "SET_COOKIE",
  GET_COOKIE: "GET_COOKIE",
  PARENT_REDIRECT: "PARENT_REDIRECT",
  CART_OPEN: "FLO_CART_OPEN",
  CART_UPDATE: "FLO_CART_UPDATE",
  ITEM_UPDATE: "FLO_ITEM_UPDATE",
  COUPONS_UPDATED: "FLO_COUPONS_UPDATED",
  EXIT_CHECKOUT: "FLO_EXIT_CHECKOUT",
  CART_TO_CHECKOUT: "FLO_CART_TO_CHECKOUT",
  CLEAR_CART_AND_CLOSE: "CLEAR_CART_AND_CLOSE",
  LOGOUT_FROM_CHECKOUT: "LOGOUT_FROM_CHECKOUT",
  // DPDPA: with consent enforced, a checkout logout logs the store out too.
  FLO_STORE_LOGOUT: "FLO_STORE_LOGOUT",
  // DPDPA: the store logged out with consent enforced, so the checkout session ends too.
  FLO_LOGOUT_FROM_STORE: "FLO_LOGOUT_FROM_STORE",
  FLO_LOGGED_OUT_FROM_STORE: "FLO_LOGGED_OUT_FROM_STORE",
  LOGIN_FROM_CHECKOUT: "LOGIN_FROM_CHECKOUT",
  SSO_LOGIN_CREDENTIALS: "SSO_LOGIN_CREDENTIALS",
  SSO_LOGIN_SUCCESS: "SSO_LOGIN_SUCCESS",
  LOGIN_RE_INIT: "LOGIN_RE_INIT",
  FLO_USER_DETAILS: "FLO_USER_DETAILS",
  FLO_GHOST_USER: "FLO_GHOST_USER",
  FLO_GHOST_USER_ORDER_DATA: "FLO_GHOST_USER_ORDER_DATA",
  GET_ANALYTICS_CONFIG: "GET_ANALYTICS_CONFIG",
  SSO_LOGIN_CHECK: "SSO_LOGIN_CHECK",
  FLO_MARKETING_POPUP_CLOSED: "FLO_MARKETING_POPUP_CLOSED",
  FLO_MARKETING_POPUP_CLOSED_LOGGED_IN: "FLO_MARKETING_POPUP_CLOSED_LOGGED_IN",
  FLO_SSO_TOKEN_RECEIVED: "FLO_SSO_TOKEN_RECEIVED",
  FLO_SSO_TOKEN_RECEIVED_GHOST: "FLO_SSO_TOKEN_RECEIVED_GHOST",
  FLO_SSO_TOKEN_NOT_RECEIVED: "FLO_SSO_TOKEN_NOT_RECEIVED",
  FLO_SSO_LOGIN_ERROR: "FLO_SSO_LOGIN_ERROR",
  FLO_NAME_EMAIL_FORM_SUBMITTED: "FLO_NAME_EMAIL_FORM_SUBMITTED",
  FLO_NAME_EMAIL_FORM_SUBMITTED_SUCCESS: "FLO_NAME_EMAIL_FORM_SUBMITTED_SUCCESS",
  FLO_NAME_EMAIL_FORM_SUBMITTED_FAILED: "FLO_NAME_EMAIL_FORM_SUBMITTED_FAILED",
  OPEN_SSO_LOGIN: "OPEN_SSO_LOGIN",
  OPEN_WISHLIST_PRODUCT: "OPEN_WISHLIST_PRODUCT",
  OPEN_PROFILE_PAGE: "OPEN_PROFILE_PAGE",
  ADD_TO_CART: "ADD_TO_CART",
  ADD_RECENTLY_VIEWED_TO_EMPTY_CART: "ADD_RECENTLY_VIEWED_TO_EMPTY_CART",
  ADD_RECENTLY_VIEWED_TO_EMPTY_CART_RESULT: "ADD_RECENTLY_VIEWED_TO_EMPTY_CART_RESULT",
  SET_CART_ITEM_COUNT: "SET_CART_ITEM_COUNT",
  REMOVE_CART_LOADING_STATE: "REMOVE_CART_LOADING_STATE",
  CLOSE_ALL_MODALS: "CLOSE_ALL_MODALS",
  FLO_SSO_COUPON_CODE_COPIED: "FLO_SSO_COUPON_CODE_COPIED",
  CHECKOUT_TIME_LIMIT_EXCEEDED: "CHECKOUT_TIME_LIMIT_EXCEEDED",
  MERCHANT_ATTRIBUTES: "MERCHANT_ATTRIBUTES",
  CLOSE_MERCHANT_SUBSCRIPTION: "CLOSE_MERCHANT_SUBSCRIPTION",
  SET_SEGMENT_ID: "SET_SEGMENT_ID",
  CLOSE_EXPERIMENTS_PREVIEW: "CLOSE_EXPERIMENTS_PREVIEW",
  COPY_TEXT: "COPY_TEXT",
  RECENTLY_VIEWED_PRODUCTS: "RECENTLY_VIEWED_PRODUCTS",
  REQUEST_RECENTLY_VIEWED_PRODUCTS: "REQUEST_RECENTLY_VIEWED_PRODUCTS",
  REMOVE_FROM_RECENTLY_VIEWED_QUEUE: "REMOVE_FROM_RECENTLY_VIEWED_QUEUE",
  GET_WEBENGAGE_DATA: "GET_WEBENGAGE_DATA",
  SET_WEBENGAGE_DATA: "SET_WEBENGAGE_DATA",
  GET_MOENGAGE_DATA: "GET_MOENGAGE_DATA",
  SET_MOENGAGE_DATA: "SET_MOENGAGE_DATA",
  CART_TO_CUSTOM_CHECKOUT: "CART_TO_CUSTOM_CHECKOUT",
  GET_CLEVERTAP_DATA: "GET_CLEVERTAP_DATA",
  SET_CLEVERTAP_DATA: "SET_CLEVERTAP_DATA",
  FLO_SSO_TOKEN_NOT_RECEIVED_GHOST: "FLO_SSO_TOKEN_NOT_RECEIVED_GHOST",
  FLO_SET_BUREAU_USER_ID: "FLO_SET_BUREAU_USER_ID",
  GET_TOKEN_ID: "GET_TOKEN_ID",
  GET_CART_DATA: "GET_CART_DATA",
  CLOSE_CART_DATA: "CLOSE_CART_DATA",
  FLO_SET_BUREAU_EVENT_ID: "FLO_SET_BUREAU_EVENT_ID",
  CLOSE_BUREAU_EVENT_ID: "CLOSE_BUREAU_EVENT_ID",
  BUREAU_USER_DATA: "BUREAU_USER_DATA",
  FLO_PAYMENT_COMPLETED: "FLO_PAYMENT_COMPLETED",
  FLO_PAYMENT_FAILED: "FLO_PAYMENT_FAILED",
  CART_LOAD_FAILURE: "CART_LOAD_FAILURE",
  GET_CUSTOM_USER_DATA: "GET_CUSTOM_USER_DATA",
  SET_CUSTOM_USER_DATA: "SET_CUSTOM_USER_DATA",
  WISHLIST_ADD_ITEM: "WISHLIST_ADD_ITEM",
  WISHLIST_REMOVE_ITEM: "WISHLIST_REMOVE_ITEM",
  WISHLIST_GET_ITEMS: "WISHLIST_GET_ITEMS",
  WISHLIST_ITEM_ADDED: "WISHLIST_ITEM_ADDED",
  WISHLIST_ITEM_REMOVED: "WISHLIST_ITEM_REMOVED",
  WISHLIST_ITEMS_DATA: "WISHLIST_ITEMS_DATA",
  WISHLIST_REFRESH: "WISHLIST_REFRESH",
  GET_LOCAL_STORAGE: "GET_LOCAL_STORAGE",
  SET_LOCAL_STORAGE: "SET_LOCAL_STORAGE",
  // DPDPA consent <-> Shopify Customer Privacy API, bridged by the storefront script.
  GET_SHOPIFY_CONSENT: "GET_SHOPIFY_CONSENT",
  SET_SHOPIFY_CONSENT: "SET_SHOPIFY_CONSENT",
  FLO_CONSENT_UPDATED: "FLO_CONSENT_UPDATED",
  SHOPIFY_CONSENT_UPDATED: "SHOPIFY_CONSENT_UPDATED",
  SET_SHOPIFY_LOGIN_VISIBLE: "SET_SHOPIFY_LOGIN_VISIBLE",
  SHOPIFY_LOGIN_RESIZE: "SHOPIFY_LOGIN_RESIZE",
  GET_SHOPIFY_LOGIN_AUTH_TOKENS: "GET_SHOPIFY_LOGIN_AUTH_TOKENS",
  SET_SHOPIFY_LOGIN_AUTH_TOKENS: "SET_SHOPIFY_LOGIN_AUTH_TOKENS",
  COMPLETE_OIDC_LOGIN: "COMPLETE_OIDC_LOGIN",
  COMPLETE_NON_OIDC_LOGIN: "COMPLETE_NON_OIDC_LOGIN",
  ACTIVATE_CHECKOUT: "FLO_ACTIVATE_CHECKOUT",
  CHECKOUT_READY: "FLO_CHECKOUT_READY",
  // Store login state. The store's access token and logout flag live on the
  // merchant's domain and are unreadable from here, so the script pushes them.
  FLO_STORE_LOGIN_STATE: "FLO_STORE_LOGIN_STATE",
  FLO_GET_STORE_LOGIN_STATE: "FLO_GET_STORE_LOGIN_STATE",
  CART_API_FAILED: "CART_API_FAILED",
  CART_API_SUCCESS: "CART_API_SUCCESS",
  // Full snapshot of the merchant's custom attributes, pushed by the store
  // script. A snapshot rather than a delta so a message lost to an unmounted
  // iframe cannot leave the two sides disagreeing.
  FLO_CUSTOM_ATTRIBUTES: "FLO_CUSTOM_ATTRIBUTES",
};

export const analyticsEvents = {
  FLO_CHECKOUT_UI_LOADED: "checkout_ui_loaded", //can't send payload and other parameters as they are generated after the event gets triggered

  FLO_ORDER_SUMMARY_BTN_CLICKED: "order_summary_clicked",

  FLO_AUTH_PHONE_LOADED: "login_page_loaded",
  FLO_AUTH_PHONE_ENTERED: "phone_entered",
  FLO_AUTH_OTP_LOADED: "otp_page_loaded",
  FLO_AUTH_OTP_ENTERED: "otp_entered",
  FLO_AUTH_OTP_RESEND: "otp_resend",
  FLO_OTP_FALLBACK_GUEST_CHECKOUT: "otp_fallback_guest_checkout_clicked",
  FLO_AUTH_LOGIN_COMPLETED: "login_completed",
  FLO_LOGOUT_BTN_CLICKED: "logged_out",
  FLO_BACK_CLICK: "back_button_clicked",
  FLO_USER_FLOW_INIT: "user_flow_initialized",
  FLO_EXIT_LOGOUT_CLICKED: "exit_logout_clicked",
  FLO_LOGOUT_CLICKED: "logout_clicked",
  FLO_COOKIE_LOGIN_ENABLED: "cookie_login_enabled",
  FLO_COOKIE_LOGIN_DISABLED: "cookie_login_disabled",

  FLO_COUPON_LOADED: "coupons_loaded",
  FLO_COUPON_ENTERED: "coupon_entered",
  FLO_COUPON_SELECTED: "coupon_selected",
  FLO_COUPON_SUCCESS: "coupon_success",
  FLO_COUPON_FAILED: "coupon_failed",
  FLO_COUPON_REMOVED: "coupon_removed",

  FLO_ADDRESSES_LIST_LOADED: "address_list_loaded",
  FLO_ADDRESS_SELECTED: "address_selected",
  FLO_ADDRESS_NEW_FORM_LOADED: "address_new_form_loaded",
  FLO_ADDRESS_NEW_FORM_SUBMITTED: "address_new_form_submitted",
  FLO_ADDRESS_EDIT_FORM_LOADED: "address_edit_form_loaded",
  FLO_ADDRESS_EDIT_FORM_SUBMITTED: "address_edit_form_submitted",
  FLO_DELIVERY_ADDRESS_CHANGE_CLICKED: "delivery_address_button_clicked",
  FLO_ADDRESS_PRIVACY_CLICKED: "address_privacy_clicked",
  FLO_ADDRESS_CONSENT_ENABLED: "address_consent_enabled",
  FLO_ADDRESS_CONSENT_DISABLED: "address_consent_disabled",

  FLO_BILLING_ADDRESS_EDIT_FORM_LOADED: "billing_address_edit_form_loaded",
  FLO_BILLING_ADDRESS_SUBMITTED: "billing_address_submitted",
  FLO_BILLING_ADDRESS_CHANGE_CLICKED: "billing_address_button_clicked",

  FLO_PAYMENT_LOADED: "payment_page_loaded",
  FLO_PAYMENT_REFRESHED: "payment_page_refreshed",
  FLO_PAYMENT_METHOD_SELECTED: "payment_method_selected",
  FLO_GC_MODAL_OPENED: "gc_modal_opened",
  FLO_GC_VALIDATION_INITIATED: "gc_validation_initiated",
  FLO_GC_VALIDATION_SUCCESS: "gc_validation_success",
  FLO_GC_VALIDATION_FAILED: "gc_validation_failed",
  FLO_GC_APPLIED: "gc_applied",
  FLO_GC_MODAL_CLOSED: "gc_modal_closed",
  FLO_GC_REMOVED: "gc_removed",
  FLO_PAYMENT_MODE_SELECTED: "payment_mode_selected",
  FLO_PAYMENT_PG_PAGE_LOADED: "payment_pg_page_loaded",
  FLO_PAYMENT_COMPLETED: "payment_completed_ui",
  FLO_ITEM_OOS: "items_out_of_stock",
  FLO_PAYMENT_TILE_SELECTED: "payment_tile_selected",
  FLO_PAYMENT_FAILED: "payment_failed_ui",

  //Cancel payment events
  FLO_CANCEL_PAYMENT_CLICKED: "cancel_payment_clicked",
  FLO_PAYMENT_CANCEL_CONFIRMATION: "payment_cancel_confirmation",
  FLO_PAYMENT_CANCELLED: "payment_cancelled",
  FLO_PAYMENT_CANCELLED_PAGE_LOADED: "payment_cancelled_page_loaded",
  FLO_TRY_OTHER_METHOD_CLICKED: "try_other_method_clicked",
  FLO_PAYMENT_RETRIED: "payment_retried",

  FLO_GUEST_LOGIN: "guest_login",
  FLO_RETURN_USER_LOGIN: "return_user_login",
  FLO_COOKIE_LOGIN: "cookie_login",

  FLO_COD_OTP_LOADED: "cod_otp_confirm_loaded",
  FLO_COD_OTP_SENT: "cod_otp_sent",
  FLO_COD_OTP_ENTERED: "cod_otp_entered",
  FLO_COD_OTP_VERIFIED: "cod_otp_verified",
  FLO_COD_OTP_SKIP: "cod_otp_skipped",
  FLO_COD_OTP_RESEND: "cod_otp_resend",
  FLO_OTP_VERIFICATION_FAILED: "otp_verification_failed",
  FLO_LOGIN_FAILED: "login_failed",

  FLO_COD_CONFIRMATION_LOADED: "cod_confirmation_loaded",
  FLO_COD_CONFIRMED: "cod_confirmed", //Event fired when COD option is confirmed in the confirmation dialog
  FLO_COD_PREPAID_CLICKED: "cod_prepaid_clicked", //Event fired when Online option is clicked in the confirmation dialog

  FLO_TERMS_AND_CONDITIONS: "terms_and_conditions_clicked",
  FLO_PRIVACY_POLICY: "privacy_policy_clicked",

  FLO_SHIPPING_HANDLES_LOADED: "shipping_handles_loaded",
  FLO_SHIPPING_HANDLE_SELECTED: "shipping_handle_selected",

  FLO_EXIT_BANNER_ACTION: "exit_banner_action",
  FLO_TRUECALLER_ACTION: "truecaller_action",

  FLO_TRUST_BADGE_CLICKED: "trust_badge_clicked",
  FLO_STORE_PAGE_VIEW: "store_page_view",

  FLO_SURVEY_FORM_LOADED: "survey_form_loaded",
  FLO_SURVEY_FORM_ANSWERED: "survey_form_answered",
  FLO_SURVEY_FORM_SKIPPED: "survey_form_skipped",
  FLO_CONFIRMATION_NUDGE_LOADED: "confirmation_nudge_loaded",

  FLO_SSO_PAGE_LOADED: "sso_page_loaded",
  FLO_SSO_PHONE_ADDED: "sso_phone_added",
  FLO_SSO_OTP_LOADED: "sso_otp_loaded",
  FLO_SSO_OTP_RESEND: "sso_otp_resend",
  FLO_SSO_OTP_ENTERED: "sso_otp_entered",
  FLO_SSO_OTP_PAGE_LOADED: "sso_otp_page_loaded",
  FLO_SSO_OTP_VERIFIED: "sso_otp_verified",
  FLO_SSO_FORM_LOADED: "sso_form_loaded",
  FLO_SSO_FORM_SUBMITTED: "sso_form_submitted",
  FLO_SSO_POP_CLOSED: "sso_pop_closed",
  FLO_SSO_LOGIN_COMPLETED: "sso_login_completed",
  // FLO_SSO_USER_VERIFY: "sso_user_verify",
  // FLO_SSO_USER_DETAILS_CHANGED: "sso_user_details_changed",
  FLO_SSO_AUTH_LOADED: "sso_auth_loaded",
  // FLO_SSO_CONSENT_REMOVED: "sso_consent_removed",

  // cart to checkout
  FLO_CHECKOUT_CLICKED: "checkout_clicked",
  FLO_ADDED_TO_CART_UI: "added_to_cart_ui",
  FLO_REMOVED_FROM_CART_UI: "removed_from_cart_ui",

  FLO_CART_QUANTITY_UPDATED: "cart_quantity_updated",
  FLO_ADDON_ADDED: "addon_added",
  FLO_ADDON_LOADED: "addon_loaded",
  ITEM_REMOVED: "item_removed",

  UPSELL_WIDGET_LOADED: "upsell_widget_loaded",
  UPSELL_PRODUCT_ADDED: "upsell_product_added",

  FLO_CHECKOUT_ISSUE_REPORTED: "checkout_issue_reported",
  FLO_CHECKOUT_MILESTONE_REACHED: "checkout_milestone_reached",

  // Wishlist events
  ADDED_TO_WISHLIST: "added_to_wishlist",
  ADDED_FROM_WISHLIST: "added_from_wishlist",
  REMOVED_FROM_WISHLIST: "removed_from_wishlist",

  // Discounted product selector events
  FLO_ITEMS_SELECTOR_LOADED: "items_selector_loaded",
  FLO_ITEMS_SELECTOR_SUBMITTED: "item_selector_submitted",

  // checkout error events
  SHIPPING_RATE_FAILURE: "shipping_rate_failure",
  FLO_CART_LOADING_TIME_REACT: "cart_load_time_react",
  UPSELL_PREVIEW_CLICKED: "upsell_preview_clicked",
  UPSELL_PREVIEW_IMAGE_CLICKED: "upsell_preview_image_clicked",
  UPSELL_PREVIEW_VARIANT_SELCTOR_CLICKED: "upsell_preview_variant_selector_clicked",
  FP_IDENTIFIED_USER: "fp_identified_user",
  FLO_TURNSTILE_ERROR: "turnstile_error",
  FLO_TURNSTILE_LOAD_TIME_EXCEEDED: "turnstile_load_time_exceeded",

  // Discount Achiever — tray that lets shoppers add qualifying items to unlock an otherwise-unavailable coupon
  FLO_DISCOUNT_ACHIEVER_OPENED: "discount_achiever_opened",
  FLO_DISCOUNT_ACHIEVER_LOADED: "discount_achiever_loaded",
  FLO_DISCOUNT_ACHIEVER_LOAD_FAILED: "discount_achiever_load_failed",
  FLO_DISCOUNT_ACHIEVER_ITEM_ADDED: "discount_achiever_item_added",
  FLO_DISCOUNT_ACHIEVER_ITEM_REMOVED: "discount_achiever_item_removed",
  FLO_DISCOUNT_ACHIEVER_SORT_CHANGED: "discount_achiever_sort_changed",
  FLO_DISCOUNT_ACHIEVER_FILTER_APPLIED: "discount_achiever_filter_applied",
  FLO_DISCOUNT_ACHIEVER_PRODUCT_VIEWED: "discount_achiever_product_viewed",
  FLO_DISCOUNT_ACHIEVER_UNLOCKED: "discount_achiever_unlocked",
  FLO_DISCOUNT_ACHIEVER_SUBMIT_CLICKED: "discount_achiever_submit_clicked",
  FLO_DISCOUNT_ACHIEVER_ADD_TO_CART_FAILED: "discount_achiever_add_to_cart_failed",
  FLO_DISCOUNT_ACHIEVER_COMPLETED: "discount_achiever_completed",
  FLO_DISCOUNT_ACHIEVER_CLOSED: "discount_achiever_closed",
  FLO_SUBSCRIPTION_MODE_SELECTED: "subscription_mode_selected",
  FLO_SUBSCRIPTION_PLAN_SELECTED: "subscription_plan_selected",
};

export const checkoutIssueReasons = {
  ADDRESS_ISSUE: "address_issue",
};

export const keys = {
  KEY_ENTER: "Enter",
};

export const analyticsTypes = {
  SF_ANALYTICS: "SHOPFLO_ANALYTICS",
  GOOGLE_ANALYTICS: "GOOGLE_ANALYTICS",
  FACEBOOK_PIXEL: "FACEBOOK_PIXEL",
  CLEVERTAP: "CLEVERTAP",
};

export enum ClevertapEvents {
  CHECKOUT_STARTED = "Checkout started",
  LOGIN_COMPLETED = "Login completed",
  ADDRESS_SUBMITTED = "Address submitted",
  PAYMENT_INITIATED = "Payment Initiated",
  COUPON_SUCCESS = "Coupon Success",
  COUPON_FAILED = "Coupon Failed",
  PAYMENT_METHOD_SELECTED = "Payment Method Selected",
  ORDER_COMPLETED = "Charged",
}

export const pageName = {
  LOADING_PAGE: "loading_page",
  LOGIN_PAGE: "login_page",
  HOME_PAGE: "home_page",
  EDIT_ADDRESS_PAGE: "edit_address_page",
  NEW_ADDRESS_PAGE: "new_address_page",
  COUPON_PAGE: "coupon_page",
  PAYMENT_PAGE: "payment_page",
  COD_OTP_PAGE: "cod_confirmation",
  LOADER: "loader_page",
};

export const paymentRetryModes = {
  change_payment: "CHANGE_PAYMENT_METHOD",
  retry_payment: "RETRY_PAYMENT",
  convert_to_cod: "CONVERT_TO_COD",
  send_whatsapp_link: "SEND_WHATSAPP_LINK",
  place_order_again: "PLACE_ORDER_AGAIN",
  close_payment: "CLOSE_PAYMENT",
};

export const routing = {
  PAYMENT_STATUS: "payment-status",
  page: "page",
};

export const trustBadges = {
  CLOCK: "clock",
  GLOBE: "globe",
  LOCK: "lock",
  RETURN: "return",
  SMILE: "smile",
  STAR: "star",
  TRUCK: "truck",
};

export const clartityTags = {
  PAGE_VISITED: "page_visited",
  MERCHANT_ID: "merchant_id",
  SHOP_NAME: "shop_name",
  CHANNEL: "channel",
};

export enum AddressSource {
  BILLING_ADDRESS = "BILLING_ADDRESS",
  SHIPPING_ADDRESS = "SHIPPING_ADDRESS",
}

export const IMAGE_CDN_URL = "https://image.shopflo.com";
export const LOGO_FALLBACK_URL = `${IMAGE_CDN_URL}/shopflo_logo_onboarding.svg`;
export const C2P_ERROR_MESSAGES = [
  "C2P token expired",
  "C2P flow timedout",
  "C2P is already completed",
  "C2P flow inaccessible due to user change",
];
export const TOKEN_ERROR = ["Checkout completed for this token...", "Checkout already completed for token"];

export const SELF_HANDLED_PAYMENT_MODES = ["LOYALTY", "REWARDS_WALLET", "FREE", "GYFTR", "GIFTCARD"];

export const apiURI: Record<string, BaseApi> = {
  KRATOS_PRIVATE: "KRATOS_PRIVATE",
  KRATOS_PUBLIC: "KRATOS_PUBLIC",
  FINGERPRINT: "FINGERPRINT",
};

export const CombDiscountConflictCode = "KR_023";

export const GSTIN_ERROR_CODE = "GE_001";

export const addressMinLength = 12;
export const FALLBACK_PRODUCT_IMAGE = `${IMAGE_CDN_URL}/dummy_product.png`;

export const FLO_MERCHANT_ID = "flo_merchant_id";
export const FLO_USER_ID = "flo_user_id";
export const ENABLE_APM_LOGS = "enable_apm_logs";
export const CART_API_FAILED = "CART_API_FAILED";

export const MOBILE_DEVICE_WIDTH = 768;

// Error codes from backend address validation (kratos KR_8xx series)
export const ADDRESS_ERROR_CODE_MAP: Record<string, string> = {
  KR_800: "invalid_address",
  KR_801: "english_only",
  KR_802: "invalid_address",
  KR_803: "invalid_phone",
  KR_804: "valid_email",
  KR_805: "invalid_address",
  KR_806: "invalid_address",
};

export const VALID_ADDRESS_FIELDS = new Set([
  "address1",
  "address2",
  "city",
  "state",
  "state_code",
  "country",
  "country_code",
  "zip",
  "name",
  "phone",
  "email",
  "gstin",
]);

export const VELOCITY_ICON_URL = "https://d2s4qtdvyggozf.cloudfront.net/velocity.png";
export const SNAPMINT_ICON_URL =
  "https://cashfreelogo.cashfree.com/assets_images/pg/cardless/64/snapmint.png";

export const GET_LOCAL_STORAGE_TIMEOUT_MS = 1000;
// Longer than local storage: the script may still be loading Shopify's consent API.
export const GET_SHOPIFY_CONSENT_TIMEOUT_MS = 3000;
export type RealEstateContext =
  | "checkout"
  | "cart"
  | "login"
  | "loginStatusCheck"
  | "marketingPopup"
  | "ghost"
  | "experiments";
